import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button, Card, CardContent } from "@/components";

import * as clientsApi from "../clients/clients.api";
import { TClientExtendedResponse } from "../clients/clients.types";
import { ClientProductsForm } from "../clients/components/client-form/client-products-form";
import * as plansApi from "../clients/plans.api";
import { useProjectPlans } from "./project-plans.hooks";
import * as projectsApi from "./projects.api";
import { TProjectDetail } from "./projects.types";

export const CreatePlanPage: React.FC = () => {
  const { clientId, projectId } = useParams<{ clientId: string; projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const state = location.state as any;
  const isEditing = state?.fromPlans && state?.planId;
  const returnPath = state?.returnPath || (clientId && projectId ? `/clients/${clientId}/projects/${projectId}` : `/clients/${clientId}`);

  // Buscar dados do cliente
  const { data: client } = useQuery<TClientExtendedResponse>({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.getClientById(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Buscar detalhes do projeto
  const {
    data: project,
    isLoading,
    error,
  } = useQuery<TProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: () => projectsApi.getProjectById(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  // Hook para gerenciar planos do projeto
  const projectPlansHook = useProjectPlans(client || null, projectId, project?.type_client);

  // Buscar dados do plano se estiver editando
  // Para planos de projeto, usar a rota específica que retorna o formato novo
  const { data: planToEdit } = useQuery({
    queryKey: ["plan", state?.planId, projectId],
    queryFn: async () => {
      if (!state?.planId || state.planId === "undefined") {
        return null;
      }
      
      if (
        projectId &&
        (project?.type_client === "affinity" || project?.type_client === "affiliate") &&
        client?.id
      ) {
        // Para planos dentro de projeto (affinity/affiliate), usar a rota que retorna o formato novo
        return projectsApi.getPlanWithCommissions(state.planId, client.id);
      } else {
        // Usar a rota antiga para planos normais
        return plansApi.getPlanById(clientId!, state.planId);
      }
    },
    enabled:
      isEditing &&
      !!state?.planId &&
      state.planId !== "undefined" &&
      !!client?.id &&
      !!projectId &&
      !!project,
    staleTime: 60_000,
  });

  // Ref para controlar se já carregou os dados do plano
  const hasLoadedPlanData = React.useRef<string | null>(null);

  // Carregar dados do plano no formulário quando estiver editando
  React.useEffect(() => {
    const currentPlanId = state?.planId;
    const shouldLoad = isEditing && 
                       planToEdit && 
                       projectPlansHook.clientProductsForm && 
                       currentPlanId &&
                       hasLoadedPlanData.current !== currentPlanId;
    
    if (shouldLoad) {
      const form = projectPlansHook.clientProductsForm;
      
      // Verificar se é formato novo (projeto de afinidade) ou formato antigo
      const isNewFormat =
        (project?.type_client === "affinity" || project?.type_client === "affiliate") &&
        planToEdit.commission_distribution &&
        typeof planToEdit.commission_distribution === "object" &&
        "main_partner" in planToEdit.commission_distribution;
      
      let planos: any[];
      
      if (isNewFormat) {
        // Formato novo: commission_distribution com main_partner, partner_participant, partner_affiliate
        const commissionDist = planToEdit.commission_distribution as any;
        const commissionAnother = (planToEdit as any).commission_another || {
          main_partner: [],
          partner_participant: [],
          partner_affiliate: [],
        };
        
        const projectParticipants = project?.organization_participants || [];

        // Mapas auxiliares para nomes
        const participantOrgMap = new Map<string, string>();
        projectParticipants.forEach((org: any) => {
          participantOrgMap.set(org.id, org.name);
        });

        const mainPartnerId = client?.id || "";
        const mainPartnerName = client?.name || "";

        const normalizePosition = (raw: any): number => {
          const n = Number(raw ?? 0);
          return n > 0 ? n : 1;
        };

        const installmentsMap = new Map<number, any>();

        const ensureInstallment = (position: number) => {
          if (!installmentsMap.has(position)) {
            installmentsMap.set(position, {
              id: `installment-${position}`,
              position,
              mainPartner: {
                id: mainPartnerId,
                name: mainPartnerName,
                agency: 0,
                commission: 0,
              },
              participatingPartners: [],
            });
          }
          return installmentsMap.get(position);
        };

        const ensureParticipatingPartner = (installment: any, orgId: string) => {
          const existingIndex = (installment.participatingPartners || []).findIndex(
            (p: any) => p.id === orgId
          );
          if (existingIndex !== -1) {
            return installment.participatingPartners[existingIndex];
          }
          const newPartner = {
            id: orgId,
            name: participantOrgMap.get(orgId) || "",
            agency: 0,
            commission: 0,
          };
          installment.participatingPartners = [
            ...(installment.participatingPartners || []),
            newPartner,
          ];
          return newPartner;
        };

        // Importante: o backend do /with-commissions pode devolver o main_partner dentro de partner_participant
        // (a separação no backend é limitada). Então tratamos ambas como fonte e inferimos pelo organization_id.
        const allCommissionTiers = [
          ...(commissionDist.main_partner || []),
          ...(commissionDist.partner_participant || []),
        ];

        allCommissionTiers.forEach((tier: any) => {
          if (!tier?.organization_id) return;
          const pos = normalizePosition(tier.position);
          const installment = ensureInstallment(pos);
          const agency = Number(tier.agency || 0) / 100;
          const commission = Number(tier.commission || 0) / 100;

          if (tier.organization_id === mainPartnerId) {
            installment.mainPartner = {
              id: mainPartnerId,
              name: mainPartnerName,
              agency,
              commission,
            };
          } else {
            const partner = ensureParticipatingPartner(installment, tier.organization_id);
            partner.agency = agency;
            partner.commission = commission;
          }
        });

        // Garantir que exista um installment para posições usadas em partner_affiliate (affiliate)
        (commissionDist.partner_affiliate || []).forEach((pa: any) => {
          const pos = normalizePosition(pa.position);
          ensureInstallment(pos);
        });

        // Garantir que todos os participantes do projeto estejam presentes em todas as parcelas (com 0 se não houver tier)
        installmentsMap.forEach((installment: any) => {
          projectParticipants.forEach((org: any) => {
            ensureParticipatingPartner(installment, org.id);
          });
        });

        const installments = Array.from(installmentsMap.values()).sort(
          (a, b) => a.position - b.position
        );

        // Mapear comissões por nível (affiliate) para o formato usado no PlanFormPage
        let affiliateCommissions: any = undefined;
        let affiliateOtherCommissions: any = undefined;
        if (project?.type_client === "affiliate") {
          const positionToIndex = new Map<number, number>();
          installments.forEach((inst: any, idx: number) => {
            positionToIndex.set(inst.position, idx);
          });

          affiliateCommissions = {};
          (commissionDist.partner_affiliate || []).forEach((pa: any) => {
            const idx = positionToIndex.get(normalizePosition(pa.position));
            if (idx === undefined) return;
            if (!affiliateCommissions[idx]) affiliateCommissions[idx] = {};
            affiliateCommissions[idx][pa.level] = {
              agency: (pa.agency || 0) / 100,
              commission: (pa.commission || 0) / 100,
            };
          });

          affiliateOtherCommissions = {};
          (commissionAnother.partner_affiliate || []).forEach((pa: any) => {
            affiliateOtherCommissions[pa.level] = {
              markup: (pa.markup || 0) / 100,
              commission: (pa.commission || 0) / 100,
            };
          });
        }
        
        // Processar commission_another para otherInstallments
        // O backend pode devolver o main_partner no array de partner_participant. Reconstituir por organization_id.
        const allAnotherTiers = [
          ...(commissionAnother.main_partner || []),
          ...(commissionAnother.partner_participant || []),
        ];

        const mainPartnerAnother = allAnotherTiers.find(
          (t: any) => t?.organization_id === mainPartnerId
        );

        const otherInstallments = {
          mainPartner: {
            id: mainPartnerId,
            name: mainPartnerName,
            // markup é armazenado em basis points (percentage) ou centavos (real_currency)
            markup: mainPartnerAnother ? Number(mainPartnerAnother.markup || 0) : 0,
            commission: mainPartnerAnother
              ? Number(mainPartnerAnother.commission || 0) / 100
              : 0,
          },
          participatingPartners: projectParticipants.map((org: any) => {
            const tier = allAnotherTiers.find(
              (t: any) => t?.organization_id === org.id
            );
            return {
              id: org.id,
              name: org.name,
              // markup é armazenado em basis points (percentage) ou centavos (real_currency)
              markup: tier ? Number(tier.markup || 0) : 0,
              commission: tier ? Number(tier.commission || 0) / 100 : 0,
            };
          }),
        };
        
        planos = [{
          id: state.planId,
          name: planToEdit.name || "",
          description: planToEdit.description || "",
          plan_features: (planToEdit as any).plan_features || [],
          markup: 0, // Não usado no formato novo
          markup_mode: 'percentage',
          products: (planToEdit.products || []).map((product: any) => ({
            id: product.product_id,
            product_id: product.product_id,
            name: product.name || "",
            type: product.type || "",
            capital_value: product.capital_value || 0,
            premium_value: product.premium_value || 0,
            description: product.description || "",
            minimum_price: null,
            plan_name: null,
            insurance_policy_extra_price_id:
              product.insurance_policy_extra_price_id ?? null,
          })),
          installments: installments,
          otherInstallments: otherInstallments,
          affiliateCommissions,
          affiliateOtherCommissions,
          commission_distribution: [], // Não usado no formato novo
          commission_lifetime_other_installments: 0,
          plan_price: planToEdit.plan_price || 0,
        }];
      } else {
        // Formato antigo: commission_distribution como array
        planos = [{
          id: state.planId,
          name: planToEdit.name || "",
          description: planToEdit.description || "",
          plan_features: (planToEdit as any).plan_features || [],
          markup: planToEdit.markup || 0,
          markup_mode: planToEdit.markup_mode || 'percentage',
          products: (planToEdit.products || []).map((product: any) => ({
            id: product.id || product.product_id,
            product_id: product.product_id || product.id,
            name: product.name || "",
            type: product.type || "",
            capital_value: product.capital_value || 0,
            premium_value: product.premium_value || 0,
            description: product.description || "",
            minimum_price: product.minimum_price || null,
            plan_name: product.plan_name || null,
            insurance_policy_extra_price_id:
              product.insurance_policy_extra_price_id ?? null,
          })),
          commission_distribution: (planToEdit.commission_distribution || []).map((comm: any) => ({
            agency: (comm.agency || 0) / 100,
            commission: (comm.commission || 0) / 100,
            position: comm.position || 0,
          })),
          commission_lifetime_other_installments: ((planToEdit as any).commission_lifetime_other_installments || 0) / 100,
          plan_price: planToEdit.plan_price || 0,
        }];
      }

      // Verificar se os dados já estão no formulário (para evitar sobrescrever)
      const currentPlanos = form.getValues("planos");
      const alreadyLoaded = currentPlanos.length > 0 && 
                           currentPlanos[0]?.id === currentPlanId &&
                           currentPlanos[0]?.products?.length > 0;
      
      if (!alreadyLoaded) {
        // (debug logs removidos)
        
        // Usar reset ao invés de setValue para garantir que todos os campos sejam atualizados
        form.reset({
          planos: planos,
        }, {
          keepDefaultValues: false,
        });
        
        // Garantir que os valores sejam aplicados, especialmente installments e otherInstallments
        form.setValue("planos", planos, {
          shouldDirty: true,
          shouldTouch: true,
        });
        
        // Verificar se os dados foram aplicados corretamente
        const verifyPlanos = form.getValues("planos");
        // (debug logs removidos)
        
        projectPlansHook.setActiveTab("tab3");
        hasLoadedPlanData.current = currentPlanId;
      }
    }
  }, [isEditing, planToEdit, state?.planId, project?.type_client, project?.organization_participants, client, projectPlansHook.clientProductsForm, projectPlansHook]);

  // Resetar o flag quando sair do modo de edição ou quando o planId mudar
  React.useEffect(() => {
    if (!isEditing || !state?.planId) {
      hasLoadedPlanData.current = null;
    }
  }, [isEditing, state?.planId]);

  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full space-y-6 p-4">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Carregando...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex flex-col w-full h-full space-y-6 p-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-2">
            <p className="text-destructive font-medium">
              Erro ao carregar projeto.
            </p>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : "Tente novamente."}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate(`/clients/${clientId}/projects/${projectId}`)}
            >
              Voltar para Detalhes do Projeto
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(`/clients/${clientId}/projects/${projectId}`)}
              size="sm"
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {isEditing ? "Editar Plano" : "Criar Plano"}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {project.project_name && (
              <>Projeto: <span className="font-medium text-foreground">{project.project_name}</span></>
            )}
          </p>
        </div>
      </div>

      {/* Formulário de Criação de Planos */}
      <ClientProductsForm
        open={true}
        onOpenChange={(open) => {
          if (!open) {
            // Usar o returnPath do state se disponível, senão voltar para detalhes do projeto
            const pathToReturn = returnPath || (clientId && projectId ? `/clients/${clientId}/projects/${projectId}` : `/clients/${clientId}`);
            navigate(pathToReturn);
          }
        }}
        form={projectPlansHook.clientProductsForm}
        searchForm={projectPlansHook.clientProductsFormSearch}
        onSubmit={isEditing 
          ? () => projectPlansHook.handleSubmitUpdatePlan(0)
          : projectPlansHook.handleSubmitCreatePlan
        }
        filteredProducts={projectPlansHook.filteredProducts}
        handleSearchProducts={projectPlansHook.handleSearchProducts}
        clearProductsFilter={projectPlansHook.clearProductsFilter}
        activeTab={projectPlansHook.activeTab}
        setActiveTab={projectPlansHook.setActiveTab}
        isEditing={isEditing}
        submitUpdatePlan={isEditing ? projectPlansHook.handleSubmitUpdatePlan : undefined}
        clientId={clientId}
        projectType={project?.type_client}
        projectPartners={project?.partner}
        projectFather={client ? { id: client.id, name: client.name } : undefined}
        projectParticipantOrganizations={project?.organization_participants}
        projectLevelHierarchical={project?.level_hierarchical || undefined}
      />
    </div>
  );
};

